'use client';

import { UploadImage } from '@/components/upload-image';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface GalleryImage {
  id: number;
  imageUrl: string;
  caption: string | null;
}

interface GalleryLightboxProps {
  images: GalleryImage[];
}

export const GalleryLightbox = ({ images }: GalleryLightboxProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const showPrev = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length)),
    [images.length]
  );
  const showNext = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i + 1) % images.length)),
    [images.length]
  );

  useEffect(() => {
    if (openIndex === null) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [openIndex, close, showPrev, showNext]);

  const activeImage = openIndex !== null ? images[openIndex] : null;

  return (
    <>
      <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
        {images.map((image, index) => (
          <figure
            key={image.id}
            role='button'
            tabIndex={0}
            onClick={() => setOpenIndex(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpenIndex(index);
              }
            }}
            aria-label={image.caption ? `View photo: ${image.caption}` : 'View photo'}
            className='group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-border/40 bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60'>
            <UploadImage
              src={image.imageUrl}
              alt={image.caption || 'Conference photo'}
              fill
              className='object-cover transition-transform duration-300 group-hover:scale-105'
              sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
            />
            {image.caption && (
              <figcaption className='absolute inset-x-0 bottom-0 bg-black/60 px-3 py-2 text-xs text-white'>
                {image.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>

      {activeImage && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 sm:p-8'
          onClick={close}
          role='dialog'
          aria-modal='true'
          aria-label='Photo viewer'>
          <button
            type='button'
            onClick={close}
            className='absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20'
            aria-label='Close'>
            <X className='h-6 w-6' />
          </button>

          {images.length > 1 && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              className='absolute left-2 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 sm:left-4'
              aria-label='Previous photo'>
              <ChevronLeft className='h-6 w-6' />
            </button>
          )}

          <div
            className='flex max-h-full w-full max-w-4xl flex-col items-center'
            onClick={(e) => e.stopPropagation()}>
            <div className='relative h-[70vh] w-full'>
              <UploadImage
                src={activeImage.imageUrl}
                alt={activeImage.caption || 'Conference photo'}
                fill
                className='object-contain'
                sizes='90vw'
              />
            </div>
            {activeImage.caption && (
              <p className='mt-4 text-center text-white/90'>{activeImage.caption}</p>
            )}
            {images.length > 1 && (
              <p className='mt-1 text-center text-sm text-white/50'>
                {openIndex! + 1} / {images.length}
              </p>
            )}
          </div>

          {images.length > 1 && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              className='absolute right-2 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 sm:right-4'
              aria-label='Next photo'>
              <ChevronRight className='h-6 w-6' />
            </button>
          )}
        </div>
      )}
    </>
  );
};
